import { Application, Container, Graphics } from "pixi.js";
import type { TaskDefinition } from "@talent/protocol";
import { SessionRecorder, TouchTracker, detectMultiTouch } from "@talent/client-telemetry";
import {
  FORWARD,
  MAX_PROGRAM_LENGTH,
  MAX_RUNS_PER_PUZZLE,
  TURN_LEFT,
  TURN_RIGHT,
  ahead,
  shortestSolution,
  type Command,
  type RobotPuzzle,
  DEMO_FORM,
} from "@talent/tasks";
import {
  ABANDONED,
  TIMED_OUT,
  animate,
  burst,
  easeOutBack,
  easeOutCubic,
  glow,
  nudge,
  prefersReducedMotion,
  ripple,
  untilAbandoned,
  untilSettled,
  wait,
} from "./game-feel.js";
import { advance, newLadder } from "./ladder.js";
import { cellTone, missTone, successChime, tick } from "./sound.js";
import { COLOR, familyColor, lighten, mix, touchMin } from "./paint.js";

/**
 * Robot engineer — queue the commands, then run them.
 *
 * The only game here where the answer is committed before any feedback arrives. Nothing can be
 * corrected mid-move, so a child who has not thought it through finds out by watching the robot
 * walk into a wall. That is the design, not a limitation: it is what makes the trial about
 * sequencing rather than about reacting.
 *
 * Three things are recorded and only the first is a score:
 *   - whether the goal was reached, inside a bounded number of runs
 *   - **attempts**: how many times the program was run. A child who plans runs once; a child who
 *     guesses runs three times and adjusts. Both may arrive.
 *   - **corrections**: commands pulled back out of the program before running it.
 *
 * The last two are the interesting ones and neither becomes a claim about the child — sequencing
 * and planning are descriptive-only in the Phase 0 triage.
 *
 * Everything on screen is a shape. Commands are an arrow and two curved arrows, never words, and
 * the robot's own nose shows which way it faces so turning is something a child sees rather than
 * something explained (v2 §15).
 */

export interface RobotTaskResult {
  reason: "completed" | "abandoned" | "ceiling" | "floor" | "error";
  trialsAttempted: number;
  trialsCorrect: number;
  rawScore: number;
}

interface RunOptions {
  canvasParent: HTMLElement;
  task: TaskDefinition;
  formId: string;
  recorder: SessionRecorder;
  generatePuzzle: (
    task: TaskDefinition,
    formId: string,
    trialIndex: number,
    difficulty: number,
  ) => RobotPuzzle;
  runProgram: (
    puzzle: RobotPuzzle,
    program: readonly Command[],
  ) => { cell: number; facing: number; trail: number[]; bumps: number };
  /** Play the wordless demonstration first. Set on a child's first time with this game. */
  showTutorial?: boolean;
  /**
   * Called when the demonstration has finished, and awaited before play begins.
   *
   * The renderer decides WHEN the child has seen enough; the shell decides how to ask them to go
   * on. That seam is why the prompt can be a real DOM dialog — proper Vazirmatn, proper RTL, the
   * design tokens — instead of something hand-drawn on a canvas in the corner it happened to fit.
   */
  /**
   * Awaited after the demonstration. Resolving with "replay" runs it again.
   *
   * A demonstration that plays once at a speed somebody else chose is lost for good to a child who
   * blinked, and their alternative is guessing through a game whose rule they never saw.
   */
  onTutorialComplete?: () => Promise<"replay" | "start">;
  signal?: AbortSignal;
  /**
   * The level to open at, from the server's suggestion for this child.
   *
   * Absent for free play, which opens at the game's own floor. Clamped into range rather than
   * trusted: it is derived from an objective's level window, which lives in a different file from
   * this task's ceiling.
   */
  startAt?: number;
}

/** What one puzzle produced, beyond right or wrong. */
interface Attempt {
  solved: boolean;
  program: Command[];
  runs: number;
  corrections: number;
}

export async function runRobotTask(opts: RunOptions): Promise<RobotTaskResult> {
  const { task, recorder, formId } = opts;
  const accent = familyColor(task.family);

  const app = new Application();
  await app.init({
    background: COLOR.stageDeep,
    resizeTo: opts.canvasParent,
    antialias: true,
    resolution: Math.min(window.devicePixelRatio || 1, 2),
    autoDensity: true,
  });
  // The play area is shared between games and must hold exactly one canvas.
  opts.canvasParent.replaceChildren(app.canvas);

  const backdrop = new Graphics();
  const glowLayer = new Container();
  const stage = new Container();
  const fx = new Container();
  app.stage.addChild(backdrop, glowLayer, stage, fx);

  const touch = new TouchTracker();
  const onPointerDown = (e: PointerEvent) => touch.down(e.pointerId);
  const onPointerUp = (e: PointerEvent) => touch.up(e.pointerId);
  app.canvas.addEventListener("pointerdown", onPointerDown);
  app.canvas.addEventListener("pointerup", onPointerUp);
  app.canvas.addEventListener("pointercancel", onPointerUp);

  const idle = (ms: number) => wait(ms, opts.signal);

  function paintBackdrop(): void {
    const w = app.renderer.width;
    const h = app.renderer.height;
    backdrop.clear();
    const bands = 24;
    for (let i = 0; i < bands; i++) {
      backdrop
        .rect(0, (h / bands) * i, w, h / bands + 1)
        .fill(mix(COLOR.stage, COLOR.stageDeep, i / bands));
    }
    backdrop.circle(w / 2, h * 0.28, Math.min(w, h) * 0.48).fill({ color: accent, alpha: 0.08 });
  }
  paintBackdrop();
  app.renderer.on("resize", paintBackdrop);

  /**
   * Draws a command as a shape. Never a letter — the whole surface is language-free.
   *
   * The two turn glyphs are ONE shape, mirrored. The first version built each from its own angles
   * and arrowhead maths, and the left one came out reversed — which is what always happens to a
   * pair defined twice. Mirroring makes disagreeing between them impossible: the left turn is the
   * right turn with every x negated, so if one reads correctly both do.
   *
   * The arc is sampled into a polyline rather than drawn with `arc()`, because an arc's sweep
   * direction is the exact thing that is easy to get backwards, and a mirrored polyline needs no
   * reasoning about it at all.
   */
  function drawCommandGlyph(g: Graphics, command: Command, size: number, color: number): void {
    const r = size * 0.26;
    if (command === FORWARD) {
      // Shaft plus head. Points up, which is the direction the robot's own nose points at rest.
      g.roundRect(-size * 0.05, -r * 0.15, size * 0.1, r * 1.15, size * 0.05).fill(color);
      g.poly([0, -r, r * 0.62, -r * 0.15, -r * 0.62, -r * 0.15]).fill(color);
      return;
    }

    // +1 draws the clockwise glyph; -1 mirrors it into the anticlockwise one.
    const sx = command === TURN_RIGHT ? 1 : -1;

    /**
     * The standard undo/redo form, with proportions measured off a reference rather than chosen.
     *
     * Four attempts failed before this, and each failure was a proportion rather than a concept.
     * The ring was small and the stroke thick, so it read as a chunky doughnut; the head was
     * anchored at the arc's start pointing forward, so the arrow pointed into its own body; and the
     * head was longer than the ring's radius, so it read as a shape in its own right rather than as
     * the arc's tip.
     *
     * What the reference actually has: a ring about a fifth of the button wide, a stroke roughly a
     * third of the ring's radius, a head about as long as that radius and slightly wider than it is
     * long, and the arc ending just past the top so the head points level rather than downhill.
     */
    const ringRadius = size * 0.2;
    const stroke = ringRadius * 0.32;
    const headLen = ringRadius * 0.95;
    const headHalf = ringRadius * 0.62;

    // Screen coordinates put y downwards, so an increasing angle sweeps clockwise. From the lower
    // right, round the bottom and the left, stopping just past the top — the gap sits on the right.
    const from = (70 * Math.PI) / 180;
    const to = (285 * Math.PI) / 180;

    const points: number[] = [];
    const steps = 32;
    for (let i = 0; i <= steps; i++) {
      const angle = from + ((to - from) * i) / steps;
      points.push(sx * Math.cos(angle) * ringRadius, Math.sin(angle) * ringRadius);
    }
    g.poly(points, false).stroke({ width: stroke, color, cap: "round", join: "round" });

    // The head grows OUTWARD from where the arc stops, base centred on that point. Mirroring x
    // turns clockwise into anticlockwise, so the pair cannot disagree about direction.
    const bx = sx * Math.cos(to) * ringRadius;
    const by = Math.sin(to) * ringRadius;
    const tangent = { x: sx * -Math.sin(to), y: Math.cos(to) };
    const normal = { x: -tangent.y, y: tangent.x };
    g.poly([
      bx + tangent.x * headLen,
      by + tangent.y * headLen,
      bx + normal.x * headHalf,
      by + normal.y * headHalf,
      bx - normal.x * headHalf,
      by - normal.y * headHalf,
    ]).fill(color);
  }

  /**
   * The bin, for clearing the program.
   *
   * A bin and not a curved undo arrow: that glyph is already the turn-left button two places away,
   * in the game whose whole subject is direction. A lid, a tapered body and two ribs is enough —
   * this is a shape children recognise long before they can read the word for it.
   */
  function drawBinGlyph(g: Graphics): void {
    const s2 = touchMin();
    const w = s2 * 0.34;
    const h = s2 * 0.34;
    const top = -h * 0.5;

    // Lid, with a handle above it.
    g.roundRect(-w * 0.62, top - h * 0.2, w * 1.24, h * 0.16, h * 0.06).fill(0xffffff);
    g.roundRect(-w * 0.22, top - h * 0.36, w * 0.44, h * 0.14, h * 0.05).fill(0xffffff);
    // Body, tapering, so it reads as a bin rather than a box.
    g.poly([
      -w * 0.5, top,
      w * 0.5, top,
      w * 0.36, top + h * 1.16,
      -w * 0.36, top + h * 1.16,
    ]).fill(0xffffff);
    // Two ribs, punched out in the panel colour beneath.
    for (const dx of [-w * 0.16, w * 0.16]) {
      g.roundRect(dx - w * 0.05, top + h * 0.24, w * 0.1, h * 0.66, w * 0.05)
        .fill(mix(COLOR.stage, COLOR.wrong, 0.3));
    }
  }

  /** The robot: a built-looking body with a nose that shows which way it faces. */
  function drawRobot(g: Graphics, size: number, facing: number, color: number): void {
    g.clear();
    const half = size * 0.34;
    g.roundRect(-half, -half, half * 2, half * 2, size * 0.12).fill(color);
    for (const dx of [-half * 0.4, half * 0.4]) {
      g.circle(dx, -half * 0.18, size * 0.07).fill(COLOR.ink);
      g.circle(dx + size * 0.025, -half * 0.18 - size * 0.025, size * 0.025).fill(0xffffff);
    }
    // The nose. Rotated with the body, so facing is visible rather than remembered.
    const nose = size * 0.2;
    g.poly([0, -half - nose, nose * 0.7, -half + 2, -nose * 0.7, -half + 2]).fill(
      COLOR.sunny,
    );
    g.rotation = (facing * Math.PI) / 2;
  }

  /**
   * Runs one puzzle and resolves with what the child did.
   *
   * Everything positional lives in `layout()`, which is called on resize as well as at start: on a
   * phone the visual viewport changes size whenever the address bar slides away, and a layout
   * measured once would leave the controls positioned for a screen that no longer exists.
   */
  function present(puzzle: RobotPuzzle): {
    attempt: Promise<Attempt>;
    simulateQueue: (command: Command) => void;
    simulateRun: () => void;
    cleanup: () => void;
  } {
    const layer = new Container();
    stage.addChild(layer);

    const blocked = new Set(puzzle.blocked);
    const board = new Graphics();
    const robot = new Graphics();
    const strip = new Container();
    const runsLeft = new Graphics();
    const controls = new Container();
    layer.addChild(board, robot, strip, runsLeft, controls);

    const program: Command[] = [];
    let corrections = 0;
    let runs = 0;
    let solved = false;
    let accepting = true;
    let running = false;
    let robotCell = puzzle.start;
    let robotFacing = puzzle.facing;
    let resolveAttempt: ((a: Attempt) => void) | null = null;

    // Geometry, refreshed by layout().
    let cell = 0;
    let boardX = 0;
    let boardY = 0;
    let chip = 0;
    let stripY = 0;

    const centreOf = (index: number) => ({
      x: boardX + (index % puzzle.size) * cell + cell / 2,
      y: boardY + Math.floor(index / puzzle.size) * cell + cell / 2,
    });

    function drawBoard(): void {
      board.clear();
      const span = cell * puzzle.size;
      board.roundRect(boardX - 8, boardY - 8, span + 16, span + 16, 20).fill({
        color: mix(COLOR.stage, COLOR.card, 0.08),
      });
      for (let i = 0; i < puzzle.size * puzzle.size; i++) {
        const x = boardX + (i % puzzle.size) * cell;
        const y = boardY + Math.floor(i / puzzle.size) * cell;
        board.roundRect(x + 3, y + 3, cell - 6, cell - 6, cell * 0.16).fill({
          color: 0xffffff,
          alpha: 0.07,
        });
        if (blocked.has(i)) {
          // A solid block, visibly different in kind from an empty tile.
          board.roundRect(x + 6, y + 6, cell - 12, cell - 12, cell * 0.14).fill({
            color: mix(COLOR.wrong, COLOR.stage, 0.45),
          });
        }
      }
      // The goal, as a star.
      const goal = centreOf(puzzle.goal);
      const r = cell * 0.26;
      const points: number[] = [];
      for (let i = 0; i < 10; i++) {
        const angle = (Math.PI / 5) * i - Math.PI / 2;
        const radius = i % 2 === 0 ? r : r * 0.44;
        points.push(goal.x + Math.cos(angle) * radius, goal.y + Math.sin(angle) * radius);
      }
      board.poly(points).fill(solved ? COLOR.correct : COLOR.sunny);
    }

    function placeRobot(): void {
      const p = centreOf(robotCell);
      robot.position.set(p.x, p.y);
      drawRobot(robot, cell, robotFacing, lighten(accent, 0.15));
    }

    /** The queued program, as tappable chips. Tapping one takes it back out. */
    function drawStrip(): void {
      strip.removeChildren().forEach((child) => child.destroy());
      const w = app.renderer.width;
      const gap = chip * 0.18;
      const total = program.length * chip + Math.max(0, program.length - 1) * gap;
      let x = (w - total) / 2 + chip / 2;

      for (const [index, command] of program.entries()) {
        const g = new Graphics();
        g.roundRect(-chip / 2, -chip / 2, chip, chip, chip * 0.28).fill({
          color: 0xffffff,
          alpha: 0.16,
        });
        drawCommandGlyph(g, command, chip, 0xffffff);
        g.position.set(x, stripY);
        g.eventMode = "static";
        g.cursor = "pointer";
        const half = chip / 2 + 6;
        g.hitArea = {
          contains: (hx: number, hy: number) => hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
        g.on("pointertap", () => {
          if (!accepting || running) return;
          program.splice(index, 1);
          corrections++;
          tick();
          drawStrip();
          void resetRobot();
        });
        strip.addChild(g);
        x += chip + gap;
      }

      // An empty slot outline, so an empty strip reads as "put commands here" and not as blank.
      if (program.length === 0) {
        const hint = new Graphics();
        hint.roundRect(-chip / 2, -chip / 2, chip, chip, chip * 0.28).stroke({
          width: 2,
          color: 0xffffff,
          alpha: 0.2,
        });
        hint.position.set(w / 2, stripY);
        strip.addChild(hint);
      }
    }

    /** Runs remaining, as pips. A numeral would be text (v2 §15). */
    function drawRunsLeft(): void {
      const w = app.renderer.width;
      const left = MAX_RUNS_PER_PUZZLE - runs;
      const r = 6;
      const step = 20;
      const y = stripY + chip * 0.85;
      let x = (w - (MAX_RUNS_PER_PUZZLE - 1) * step) / 2;
      runsLeft.clear();
      for (let i = 0; i < MAX_RUNS_PER_PUZZLE; i++) {
        runsLeft.circle(x, y, r).fill({
          color: i < left ? accent : 0xffffff,
          alpha: i < left ? 1 : 0.18,
        });
        x += step;
      }
    }

    /**
     * Puts the robot back where the next run will begin.
     *
     * The board must always show the state a run would START from. After a failed run the robot
     * was left wherever the program abandoned it — turned some other way, standing somewhere else —
     * and the child then planned their next program against that. Their new commands were correct
     * for the robot they could see and wrong for the one the run would actually use, and nothing on
     * screen could tell them why.
     *
     * So the robot is away from its start only while a program is running. Everywhere else it is
     * home, facing the way it started.
     */
    async function resetRobot(): Promise<void> {
      if (robotCell === puzzle.start && robotFacing === puzzle.facing) return;
      robotCell = puzzle.start;
      robotFacing = puzzle.facing;

      if (prefersReducedMotion()) {
        placeRobot();
        return;
      }
      const to = centreOf(puzzle.start);
      const from = { x: robot.position.x, y: robot.position.y, r: robot.rotation };
      const toR = (puzzle.facing * Math.PI) / 2;
      // Shortest way round, or a robot that started facing up spins three quarters to get back.
      const delta = ((toR - from.r + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
      await animate(260, (t) => {
        const e = easeOutCubic(t);
        robot.position.set(from.x + (to.x - from.x) * e, from.y + (to.y - from.y) * e);
        robot.rotation = from.r + delta * e;
      });
      placeRobot();
    }

    /**
     * Empties the program.
     *
     * Tapping a queued chip removes it and always did, but that could not be the primary way to
     * fix a mistake for two reasons. It is undiscoverable — nothing suggests a chip is tappable —
     * and the chips are 26 to 38 px, well under the ~76 px this age band needs, so even a child who
     * knows the trick is aiming at something too small to hit reliably. Chip removal stays for
     * surgical edits; this is the way out that is visible.
     *
     * It clears everything rather than undoing the last command, and the icon decided that. The
     * standard undo glyph is a curved arrow — which is exactly the turn-left button sitting two
     * places away, in the one game whose entire subject is direction. Two adjacent controls sharing
     * a shape in a game about left and right is a worse problem than re-tapping four commands.
     */
    function clearProgram(): void {
      if (!accepting || running || program.length === 0) {
        tick();
        return;
      }
      // Counted per command removed rather than as one act. "How much was reworked" is the signal;
      // a child who wipes eight commands and one who removes a single chip did not do the same
      // thing, and recording both as 1 would say they did.
      corrections += program.length;
      program.length = 0;
      tick();
      drawStrip();
      void resetRobot();
    }

    /** Appends a command. Shared by the buttons and by the demonstration. */
    function queueCommand(command: Command): void {
      if (!accepting || running) return;
      if (program.length >= MAX_PROGRAM_LENGTH) {
        // Refused audibly rather than silently: a button that does nothing reads as broken.
        tick();
        return;
      }
      program.push(command);
      cellTone(program.length + 2, { volume: 0.12 });
      drawStrip();
    }

    /** Steps the program, one command at a time, so a child can see where it went wrong. */
    async function execute(): Promise<void> {
      if (running || program.length === 0) return;
      running = true;
      robotCell = puzzle.start;
      robotFacing = puzzle.facing;
      placeRobot();
      await idle(240);

      for (const [index, command] of program.entries()) {
        if (opts.signal?.aborted) break;
        // Highlight the command being executed, so the program and the movement are visibly
        // linked — that connection is the whole lesson of the game.
        const chipGraphic = strip.children[index];
        if (chipGraphic) void animate(200, (t) => (chipGraphic.alpha = 1 - 0.55 * Math.sin(t * Math.PI)));

        if (command === TURN_LEFT || command === TURN_RIGHT) {
          robotFacing = (robotFacing + (command === TURN_RIGHT ? 1 : 3)) % 4;
          cellTone(robotFacing + 2, { volume: 0.1 });
          if (prefersReducedMotion()) placeRobot();
          else {
            const from = robot.rotation;
            const to = (robotFacing * Math.PI) / 2;
            // Shortest way round, or turning left from up spins three quarters the wrong way.
            const delta = ((to - from + Math.PI * 3) % (Math.PI * 2)) - Math.PI;
            await animate(220, (t) => (robot.rotation = from + delta * easeOutCubic(t)));
            placeRobot();
          }
        } else {
          const next = ahead(robotCell, robotFacing, puzzle.size, blocked);
          if (next === null) {
            // A bump. Shown, not hidden: this is where the child learns what their program did.
            missTone();
            await nudge(robot, 6);
          } else {
            robotCell = next;
            cellTone(next % 9);
            const to = centreOf(next);
            if (prefersReducedMotion()) placeRobot();
            else {
              const from = { x: robot.position.x, y: robot.position.y };
              await animate(220, (t) => {
                const e = easeOutCubic(t);
                robot.position.set(from.x + (to.x - from.x) * e, from.y + (to.y - from.y) * e);
              });
            }
            ripple(fx, to.x, to.y, accent, cell * 0.6);
          }
        }
        await idle(90);
      }

      runs++;
      drawRunsLeft();
      running = false;

      if (robotCell === puzzle.goal) {
        solved = true;
        drawBoard();
        accepting = false;
        const goal = centreOf(puzzle.goal);
        successChime();
        burst(fx, goal.x, goal.y, [accent, lighten(accent, 0.4), COLOR.sunny], 22);
        await idle(700);
        resolveAttempt?.({ solved: true, program: [...program], runs, corrections });
        return;
      }

      if (runs >= MAX_RUNS_PER_PUZZLE) {
        accepting = false;
        await idle(400);
        resolveAttempt?.({ solved: false, program: [...program], runs, corrections });
        return;
      }

      // The program stays — editing it after watching it fail is the point — but the robot goes
      // home, so the child edits against the position the next run will actually start from.
      await idle(420);
      await resetRobot();
    }

    function buildControls(): void {
      controls.removeChildren().forEach((child) => child.destroy());
      const w = app.renderer.width;
      const h = app.renderer.height;
      const button = touchMin();

      /**
       * Two rows, because five buttons do not fit one.
       *
       * At 76 px each — the floor for this age band — five in a row need 380 px before any gaps,
       * on a screen that is often 360 px wide. Shrinking them below the floor to fit would trade a
       * layout problem for a mis-tap problem, and a mis-tap here queues a command the child did not
       * choose, which they then have to notice and undo.
       *
       * The split is also the right grouping: the three moves you can add, then the two things you
       * can do with what you have added.
       */
      const rowGap = 12;
      const bottom = h - 20;
      const actY = bottom - button / 2;
      const buildY = actY - button - rowGap;

      const make = (
        paint: (g: Graphics) => void,
        onTap: () => void,
        tint: number,
        x: number,
        y: number,
      ) => {
        const g = new Graphics();
        g.roundRect(-button / 2, -button / 2, button, button, 22).fill(tint);
        paint(g);
        g.position.set(x, y);
        g.eventMode = "static";
        g.cursor = "pointer";
        const half = button / 2 + 4;
        g.hitArea = {
          contains: (hx: number, hy: number) => hx >= -half && hy >= -half && hx <= half && hy <= half,
        };
        g.on("pointertap", onTap);
        controls.addChild(g);
      };

      const row = (count: number) => {
        const gap = Math.max(10, Math.min(24, (w - button * count) / (count + 1)));
        const width = button * count + gap * (count - 1);
        return { start: (w - width) / 2 + button / 2, step: button + gap };
      };

      const faint = mix(COLOR.stage, COLOR.card, 0.14);
      const build = row(3);
      const commands: Command[] = [FORWARD, TURN_LEFT, TURN_RIGHT];
      commands.forEach((command, i) => {
        make(
          (g) => drawCommandGlyph(g, command, button, 0xffffff),
          () => queueCommand(command),
          faint,
          build.start + i * build.step,
          buildY,
        );
      });

      const act = row(2);
      make(drawBinGlyph, clearProgram, mix(COLOR.stage, COLOR.wrong, 0.3), act.start, actY);
      // Run, in the family colour, because it is the one button that commits.
      make(
        (g) => {
          const r = button * 0.2;
          g.poly([-r * 0.6, -r, r * 0.9, 0, -r * 0.6, r]).fill(COLOR.ink);
        },
        () => {
          if (!accepting || running || program.length === 0) {
            tick();
            return;
          }
          void execute();
        },
        accent,
        act.start + act.step,
        actY,
      );
    }

    function layout(): void {
      const w = app.renderer.width;
      const h = app.renderer.height;
      const button = touchMin();
      // Bottom up: controls, then the strip, then whatever is left for the board.
      // Two control rows plus their gap and the margin below them.
      const controlsBlock = button * 2 + 12 + 40;
      chip = Math.min(38, Math.max(26, (w * 0.88 - 11 * 4) / MAX_PROGRAM_LENGTH));
      const stripBlock = chip + 40;
      const boardSpan = Math.min(w * 0.88, h - controlsBlock - stripBlock - 40);

      cell = boardSpan / puzzle.size;
      boardX = (w - boardSpan) / 2;
      boardY = 24;
      stripY = boardY + boardSpan + 34;

      drawBoard();
      placeRobot();
      drawStrip();
      drawRunsLeft();
      buildControls();
    }

    layout();
    /**
     * The pieces arrive rather than appearing.
     *
     * A board that is simply there reads as a diagram; one that is dealt out reads as a game being set
     * up for you — and it gives a child a moment to look before anything is expected of them. Staggered
     * so it is a sequence, and skipped under `prefers-reduced-motion`, where the whole point of the
     * setting is that things do not fly around.
     */
    if (!prefersReducedMotion()) {
      board.scale.set(0);
      robot.scale.set(0);
      void animate(300, (t) => {
        const s = easeOutBack(t);
        board.scale.set(s);
        robot.scale.set(s);
      });
      // The controls follow the board, so the child looks at the puzzle before the buttons.
      controls.children.forEach((button, i) => {
        button.scale.set(0);
        void idle(220 + i * 70).then(() => animate(240, (t) => button.scale.set(easeOutBack(t))));
      });
    }

    app.renderer.on("resize", layout);
    const goalPoint = centreOf(puzzle.goal);
    glow(glowLayer, goalPoint.x, goalPoint.y, cell * 0.6, COLOR.sunny, 1400);

    if (!prefersReducedMotion()) {
      layer.alpha = 0;
      void animate(300, (t) => (layer.alpha = easeOutBack(Math.min(1, t))));
    }

    const attempt = new Promise<Attempt>((resolve) => {
      resolveAttempt = resolve;
    });

    return {
      attempt,
      /**
       * The moves the demonstration plays, driving the REAL handlers rather than miming them.
       *
       * A scripted imitation of queueing and running would drift from the code it is teaching, and
       * a tutorial that shows something the game no longer does is worse than none.
       */
      simulateQueue: (command: Command) => queueCommand(command),
      simulateRun: () => void execute(),
      cleanup: () => {
        accepting = false;
        app.renderer.off("resize", layout);
        layer.destroy({ children: true });
      },
    };
  }

  // ---------------------------------------------------------------------------
  // Trial loop
  // ---------------------------------------------------------------------------
  // The level to open at comes from the server's suggestion — the objective the child is working
  // on — and is clamped into this task's range by `newLadder`.
  let ladder = newLadder(task, opts.startAt);
  let difficulty = ladder.difficulty;
  let trialIndex = 0;
  let correctCount = 0;
  let reason: RobotTaskResult["reason"] = "completed";

  /**
   * A wordless demonstration, before anything is scored.
   *
   * This game hides its rule more thoroughly than any of the others: nothing on screen says that
   * the buttons BUILD a list rather than moving the robot, and a child who taps forward and sees
   * nothing move concludes the game is broken. It cannot be written down either — no text on this
   * surface (v2 §15).
   *
   * So the game plays a whole turn itself: commands appear in the strip one at a time, then run is
   * pressed and the robot walks. Queue, then go — shown end to end in a few seconds.
   *
   * It uses the real handlers rather than a scripted imitation, so a tutorial cannot drift from
   * the game it teaches. Its own puzzle under a `demo` form id, never a scored one, and it is not
   * recorded: every child sees the same demonstration, so it says nothing about any of them.
   */
  async function playTutorial(): Promise<void> {
    // Looped: the child can watch it again as many times as they want.
    let choice: "replay" | "start" = "start";
    do {
      const demo = opts.generatePuzzle(task, DEMO_FORM, 0, task.measurement.minDifficulty);
      const solution = shortestSolution(demo);
      if (!solution) return;

      const shown = present(demo);
      // A beat to see the robot and where it has to get to, before anything moves.
      await idle(900);
      for (const command of solution) {
        if (opts.signal?.aborted) break;
        shown.simulateQueue(command);
        // Slow enough that a child can connect each button to the chip that appears.
        await idle(420);
      }
      if (!opts.signal?.aborted) {
        await idle(500);
        shown.simulateRun();
        await untilAbandoned(shown.attempt, opts.signal);
        choice = (await opts.onTutorialComplete?.()) ?? "start";
      }
      shown.cleanup();
      if (opts.signal?.aborted) return;
    } while (choice === "replay");
  }

  recorder.enterTask(task.id, task.version);
  await recorder.record("task_start", {
    formId,
    startingDifficulty: difficulty,
    narrative: task.presentation.narrative,
    calibrationVersion: task.measurement.calibrationVersion,
  });

  try {
    if (opts.showTutorial && !opts.signal?.aborted) await playTutorial();
    for (;;) {
      if (opts.signal?.aborted) {
        reason = "abandoned";
        break;
      }
      const puzzle = opts.generatePuzzle(task, formId, trialIndex, difficulty);

      recorder.setInTrial(true);
      await recorder.record("trial_start", {
        trialIndex,
        difficulty,
        // The puzzle, so the trial is re-solvable from the record alone: grid size, start, facing,
        // goal, then the blocked cells.
        stimulus: [puzzle.size, puzzle.start, puzzle.facing, puzzle.goal, ...puzzle.blocked],
      });

      const started = performance.now();
      const trial = present(puzzle);
      const outcome = await untilSettled(trial.attempt, opts.signal, task.measurement.trialTimeoutMs);
      const elapsed = performance.now() - started;
      trial.cleanup();

      if (outcome === ABANDONED || outcome === TIMED_OUT) {
        /**
         * Stopping and leaving both end the sitting, and only one of them is recorded.
         *
         * A timeout means the child put the phone down mid-trial. It is `abandoned` rather than a
         * wrong answer on purpose: we know they stopped and we do not know they could not do it, and
         * scoring silence as failure is the mistake `stopAfterErrors` used to make.
         */
        if (outcome === TIMED_OUT) {
          await recorder.record("trial_timeout", { trialIndex, elapsedMs: elapsed });
        }
        recorder.setInTrial(false);
        reason = "abandoned";
        break;
      }

      if (outcome.solved) correctCount++;

      await recorder.record("trial_response", {
        trialIndex,
        // The program as written. Kept whole rather than reduced to a length, because a program
        // that reaches the goal by a long route and one that goes straight there are different
        // answers with the same score.
        response: outcome.program,
        correct: outcome.solved,
        responseMs: elapsed,
        corrections: outcome.corrections,
        // The headline signal of this game: one run means the route was reasoned out, three means
        // it was found by watching. Never turned into a claim.
        attempts: outcome.runs,
      });

      const multi = detectMultiTouch(touch.peak);
      if (multi) await recorder.record("input_anomaly", multi);
      touch.resetPeak();

      recorder.setInTrial(false);
      trialIndex++;

      /**
       * The teaching policy lives in `ladder.ts`, not here.
       *
       * A streak raises the level, errors lower it, and the sitting ends when the trials run out —
       * never because a child was struggling (R-5). It was copied into all ten renderers before,
       * which is nine chances for one game to end up quietly stricter than the rest.
       */
      const step = advance(task, ladder, outcome.solved);
      if (step.change) await recorder.record("difficulty_change", step.change);
      ladder = step;
      difficulty = step.difficulty;
      if (step.finished) {
        reason = "completed";
        break;
      }
      await idle(260);
    }
  } catch {
    reason = "error";
  } finally {
    await recorder.record("task_end", {
      reason,
      trialsAttempted: trialIndex,
      rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
    });
    recorder.exitTask();
    app.renderer.off("resize", paintBackdrop);
    app.canvas.removeEventListener("pointerdown", onPointerDown);
    app.canvas.removeEventListener("pointerup", onPointerUp);
    app.canvas.removeEventListener("pointercancel", onPointerUp);
    app.destroy(true, { children: true });
  }

  return {
    reason,
    trialsAttempted: trialIndex,
    trialsCorrect: correctCount,
    rawScore: trialIndex > 0 ? correctCount / trialIndex : 0,
  };
}
